import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddIcon from "@mui/icons-material/Add";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Grid2 as Grid,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowSelectionModel, GridRowsProp } from "@mui/x-data-grid";
import { GridGroupingColDefOverride, useGridApiRef } from "@mui/x-data-grid-premium";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useOutletContext } from "react-router";
import Viewer from "react-viewer";
import { debounce } from "throttle-debounce";
import AssetClassChip from "../../../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import PosterImage from "../../../../../Components/Misc/PosterImage";
import AssetStatus from "../../../../../Components/Misc/Status/AssetStatus/asset-status";
import { ModalContext } from "../../../../../Components/Modals";
import ModalPreview, { ModalPreviewProps } from "../../../../../Components/Modals/ModalPreview/modal-video-preview";
import AssetPanel from "../../../../../Components/Panels/AssetPanel/asset-panel";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableCellBool from "../../../../../Components/Table/TableCellBool";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import TreeViewActions from "../../../../../Components/TreeView/TreeViewActions/tree-view-actions";
import { cacheService, mediaService, thumbnailService } from "../../../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import { DbMetadataFieldInfos } from "../../../../../Core/Api/Types/db-metadata-field-infos";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import {
  AreAllObjectsShareable,
  CountAssetsInContent,
  CountChildRecursivelyObjectById,
  FlattenObjectListFromGraph,
  GetAssetContentId,
  GetChildsRecursivelyByIds,
  GetMetadataByTagName,
} from "../../../../../Core/Utils/asset-object-utils";
import { GetObject, IsObjectLoading } from "../../../../../Redux/Reducers/redux-slice-asset";
import { routePaths } from "../../../../../Router/router";
import { Distinct, GetObjectStatusLabel, UserHasAcl } from "../../../../../Utils/helpers";
import useBasketActions from "../../../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../../../Utils/Hooks/useCollectionActions";

import ModalCreateContent, {
  ModalCreateContentElementProps,
} from "../../../../../Components/Modals/ModalCreateContent/modal-create-content";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import KeyIcon from "@mui/icons-material/Key";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import AssetTypeFromObjectChip from "../../../../../Components/Misc/Chips/AssetChip/asset-type-chip-from-object";
import { TableCellLongTypography } from "../../../../../Components/Table/TableCellTypography/table-cell-typography";
import {
  BarChartOutlined,
  HighQualityOutlined,
  Inventory2Outlined,
  PlayArrowOutlined,
  WorkspacesOutlined,
} from "@mui/icons-material";
import FormMultiSelect from "../../../../../Components/Forms/FormMultiSelect/form-multi-select";
import { DataGridProProps } from "@mui/x-data-grid-pro";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../../Components/Table/table-uuids";
import AssetTreeView from "../../../../../Components/TreeView/TitleTreeView/asset-tree-view-2";
import { GetSelectedItems, TreeContext } from "../../../../../Redux/Reducers/redux-slice-tree";
import { useAppSelector } from "../../../../../Redux/redux-store";

const CUSTOM_GROUPING_COL_DEF: GridGroupingColDefOverride = {
  headerName: "",
  leafField: "id",
  width: 0,
  flex: CellSize.medium,
  renderCell: () => <></>,
};

export interface ContentFilterObject {
  key: number;
  contentName: string;
  childNumber: number;
  isDeleted: boolean;
  episodes?: number[];
}

type TabType = "tree" | "table";

function AdminTitleAssets() {
  // #region states, redux, context
  const { t } = useTranslation();
  const navigate = useNavigate();
  // tab state
  const [selectedDisplayTab, setSelectedDisplayTab] = useState<TabType>("table");
  const apiGrid = useGridApiRef();
  // filters
  const [searchValue, setSearchValue] = useState("");
  const [filtersData, setFiltersData] = useState<{
    contentFilterOptions: ContentFilterObject[];
    typeFilterOptions: ObjectType[];
    subTypeFilterOptions: string[];
    classFilterOptions: ObjectClass[];
    statusFilterOptions: ObjectStatus[];
  }>({
    contentFilterOptions: [],
    typeFilterOptions: [],
    subTypeFilterOptions: [],
    classFilterOptions: [],
    statusFilterOptions: [],
  });

  const [recoveryFilter, setRecoveryFilter] = useState<boolean>(false);
  const [byPassFilter, setByPassFilter] = useState<boolean>(false);
  const [videoRefFilter, setVideoRefFilter] = useState<boolean>(false);
  const [fullQcFilter, setFullQcFilter] = useState<boolean>(false);
  const [contentFilter, setContentFilter] = useState<ContentFilterObject[]>([]);
  const [typeFilter, setTypeFilter] = useState<ObjectType[]>([]);
  const [subTypeFilter, setSubTypeFilter] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState<ObjectClass[]>([]);
  const [statusFilter, setStatusFilter] = useState<ObjectStatus[]>([]);
  const [posterViewerVisible, setPosterViewerVisible] = useState<number | undefined>(undefined);

  // table
  const [rows, setDataGrid] = React.useState<GridRowsProp>([]);
  const selectedTreeItems = useAppSelector(GetSelectedItems(TreeContext.AssetHierarchy));
  const [clickedAssetId, setClickedAssetId] = useState<number | undefined>(undefined);
  const [openAssetPanel, setOpenAssetPanel] = useState(false);

  // Redux
  const title = useSelector(GetObject);
  const isLoading = useSelector(IsObjectLoading);
  const isSeason = useMemo(() => {
    if (!title) return false;

    return GetMetadataByTagName(title, "titleType")?.value === "Season";
  }, [title]);

  const { addObjectByIdToBasket } = useBasketActions();
  const { AddItemsToCollection } = useCollectionActions();

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItems, setSelectedItems, setCanSelectedItemsBeShared } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
    selectedItems: DbAssetObjectInfos[];
    setCanSelectedItemsBeShared: React.Dispatch<React.SetStateAction<boolean>>;
  }>();

  // modal context
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Acl
  const aclOrder = UserHasAcl(AclTypes.aclSectionOrderAccess);
  const aclViewDeleted = UserHasAcl(AclTypes.aclMetadataAssetViewDeleted);
  const aclTimetool = UserHasAcl(AclTypes.aclTimetool);

  // #endregion
  // #region functions
  // Initialize data
  const mapDataToGrid = (objs: DbAssetObjectInfos[], title: DbAssetObjectInfos): any => {
    let output: any = [];

    if (title === undefined) return output;

    let season = GetMetadataByTagName(title, "titleType", metadata)?.value === "Season";
    let refs: number[] = [];

    title.childObjects?.forEach((child) => {
      let match = child.metadata?.find((x) => x.fieldName === "refVideoAssetIds")?.value;
      if (match) (match as number[]).forEach((id) => refs.push(id));
    });

    objs.forEach((x) => {
      if (!x) return;
      let format = GetMetadataByTagName(x, "techCodec", metadata)?.value.replace("_", " ");
      if (format === undefined) format = "Undefined";
      else {
        if (x.type === ObjectType.Video)
          format = format + " " + GetMetadataByTagName(x, "videoProfile", metadata)?.value;
      }

      let isFullQcDone = GetMetadataByTagName(x, "fullQcDone", metadata)?.value;
      let isLegacyImport = GetMetadataByTagName(x, "isLegacyImport", metadata)?.value;
      let isIngestQCBypassed = GetMetadataByTagName(x, "ingestQCBypassed", metadata)?.value;
      let content = GetAssetContentId(title!, x);

      output.push({
        id: x.id,
        contentName: content?.name,
        contentId: content?.id,
        contentSubType: GetMetadataByTagName(x, "contentSubType", metadata)?.value,
        objectType: x.type!,
        format: format,
        isFullQcDone: isFullQcDone ? isFullQcDone : false,
        isLegacyImport: isLegacyImport ? isLegacyImport : false,
        isIngestQCBypassed: isIngestQCBypassed ? true : false,
        isDeleted: x.isDeleted,
        objectClass: x.classification,
        clips: x.metadata?.find((m) => m.fieldName === "childClipNb")?.value ?? 0,
        ep: !season ? "-" : GetMetadataByTagName(x, "serieEpisodeNumberProduction", metadata)?.value,
        status: GetMetadataByTagName(x, "isDisabled", metadata)?.value ? ObjectStatus.Disabled : x.status,
        name: x.name,
        ref: refs.includes(x.id),
        asset: x,
        isGroup: x.layer === ObjectLayer.Group || (x.allParentObjectIds && x.allParentObjectIds.length > 2),
      });
    });

    return output;
  };

  const getTreeDataPath: DataGridProProps["getTreeDataPath"] = useCallback(
    (row) => {
      // If asset is a direct child of a group, return the group id and the asset id
      const lastParentId = row.asset.allParentObjectIds[row.asset.allParentObjectIds.length - 1];
      if (lastParentId) {
        const lastParent = rows.find((r) => r.id === lastParentId);
        if (lastParent && lastParent.asset.classification === ObjectClass.Group) {
          return [lastParent.id, row.id];
        }
      }

      return [row.id];
    },
    [rows]
  );

  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();

  const ExpandableButton = (id: any) => {
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
      apiGrid.current.setRowChildrenExpansion(id.id, !expanded);
    };

    return (
      <GridActionsCellItem
        icon={
          expanded ? (
            <UnfoldLessIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
          ) : (
            <UnfoldMoreIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
          )
        }
        label={expanded ? t("table:cellLabel.collapse") : t("table:cellLabel.expand")}
        title={expanded ? t("table:cellLabel.collapse") : t("table:cellLabel.expand")}
        onClick={handleExpandClick}
      />
    );
  };

  // Data grid
  type Row = (typeof rows)[number];
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) => (
          <>
            {!data?.row?.isDeleted || (data?.row?.isDeleted && aclViewDeleted) ? (
              <>
                {data?.row?.asset &&
                  data?.row?.asset.allParentObjectIds !== null &&
                  data?.row?.asset.allParentObjectIds !== undefined &&
                  data?.row?.asset.allParentObjectIds.length > 2 && (
                    <SubdirectoryArrowRightIcon color="disabled" fontSize="medium" />
                  )}
                <Link
                  color="primary.text"
                  underline="hover"
                  component={RouterLink}
                  to={`${routePaths.asset.path}/${data.id}`}
                >
                  {data.value}
                </Link>
              </>
            ) : (
              data.id
            )}
          </>
        ),
      },
      {
        field: "name",
        headerName: t("common:assetUpperCase"),
        minWidth: CellSize.xxlarge,
        flex: CellSize.xxlarge,
        valueFormatter: (data: any, row: any) => {
          return data;
        },
        renderCell: (data: Row) => (
          <Stack pt={1} pb={1} gap={1} width={"100%"}>
            <TableCellLongTypography variant="body1" data={data.value} color="text.primary" />
            <Stack direction="row">
              <Chip
                label={data.row.contentName + (data.row.contentSubType ? " / " + data.row.contentSubType : "")}
                color="info"
                size="small"
              />
              {data.row.objectType === ObjectType.DcpPackage &&
                GetMetadataByTagName(data.row.asset, "dcpIsCrypted")?.value &&
                data.row.asset.childCount !== null &&
                data.row.asset.childCount > 0 && (
                  <Chip
                    size={"small"}
                    variant={"outlined"}
                    label={t("chip:label.dkdmMasterKeyFound")}
                    color={"info"}
                    sx={{ ml: 1 }}
                    icon={<KeyIcon fontSize="small" />}
                  />
                )}
              {data.row?.isFullQcDone && (
                <Chip
                  size={"small"}
                  variant={"outlined"}
                  label={t("common:fullQC")}
                  color={"primary"}
                  sx={{ ml: 1 }}
                  icon={<CheckIcon fontSize="small" />}
                />
              )}
              {data?.row?.isLegacyImport && (
                <Chip
                  size={"small"}
                  variant={"outlined"}
                  label={t("common:legacyImport")}
                  color={"warning"}
                  sx={{ ml: 1 }}
                />
              )}
              {data?.row?.isIngestQCBypassed && (
                <Chip
                  size={"small"}
                  variant={"outlined"}
                  label={t("common:bypassed")}
                  color={"warning"}
                  sx={{ ml: 1 }}
                />
              )}
            </Stack>
          </Stack>
        ),
      },
      {
        field: "isLegacyImport",
        headerName: t("common:legacyImport"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueFormatter: (data: any, row: any) => {
          if (data) return "True";
          else return "False";
        },
        renderCell: (data: Row) =>
          data?.row?.asset.layer !== ObjectLayer.Group ? (
            data.value && (
              <Chip
                size={"small"}
                variant={"outlined"}
                label={t("common:legacyImport")}
                color={"warning"}
                sx={{ ml: 1 }}
              />
            )
          ) : (
            <></>
          ),
      },
      {
        field: "isIngestQCBypassed",
        headerName: t("common:bypassed"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueFormatter: (data: any, row: any) => {
          if (data) return "True";
          else return "False";
        },
        renderCell: (data: Row) =>
          data?.row?.asset.layer !== ObjectLayer.Group ? (
            data.value && (
              <Chip size={"small"} variant={"outlined"} label={t("common:bypassed")} color={"warning"} sx={{ ml: 1 }} />
            )
          ) : (
            <></>
          ),
      },
      {
        field: "isFullQcDone",
        headerName: t("common:fullQC"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueFormatter: (data: any, row: any) => {
          if (data) return "True";
          else return "False";
        },
        renderCell: (data: Row) =>
          data?.row?.asset.layer !== ObjectLayer.Group ? (
            data.value && (
              <Chip size={"small"} variant={"outlined"} label={t("common:fullQC")} color={"warning"} sx={{ ml: 1 }} />
            )
          ) : (
            <></>
          ),
      },
      {
        field: "contentName",
        headerName: t("table:headerName.contentType"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueFormatter: (data: any, row: any) => {
          return data;
        },
        renderCell: (data: Row) =>
          data?.row?.asset.layer !== ObjectLayer.Group ? (
            <Chip label={data.value} color="default" size="small" />
          ) : (
            <></>
          ),
      },
      {
        field: "contentSubType",
        headerName: t("table:headerName.contentSubType"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueFormatter: (data: any, row: any) => {
          return data;
        },
        renderCell: (data: Row) =>
          data?.row?.asset?.layer !== ObjectLayer.Group ? (
            <Chip label={data.value} color="default" size="small" />
          ) : (
            <></>
          ),
      },
      {
        field: "ep",
        headerName: "EP",
        minWidth: CellSize.xsmall,
        flex: CellSize.xsmall,
        renderCell: (data: Row) =>
          data?.row?.asset?.layer !== ObjectLayer.Group ? (
            <Chip size="small" label={data.value} color="default" />
          ) : (
            <></>
          ),
      },
      {
        field: "objectType",
        headerName: t("common:type"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "singleSelect",
        valueOptions: filtersData.typeFilterOptions,
        renderCell: (data: Row) => <AssetTypeFromObjectChip size="small" obj={data?.row?.asset} />,
      },
      {
        field: "objectClass",
        headerName: t("common:class"),
        type: "singleSelect",
        valueOptions: filtersData.classFilterOptions,
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) => <AssetClassChip size="small" label={data.value} classification={data.value} />,
      },
      {
        field: "format",
        headerName: t("common:format"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) =>
          data?.row?.asset?.layer !== ObjectLayer.Group ? (
            <Chip size="small" label={data.value} color="default" />
          ) : (
            <></>
          ),
      },
      {
        field: "clips",
        headerName: t("common:clips"),
        minWidth: CellSize.xsmall,
        flex: CellSize.xsmall,
        type: "number",
        renderCell: (data: Row) =>
          data?.row?.asset?.layer !== ObjectLayer.Group ? <Chip size="small" label={data.value} /> : <></>,
      },
      {
        field: "ref",
        headerName: t("table:headerName.ref"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "boolean",
        renderCell: (data: Row) =>
          data?.row?.asset?.layer !== ObjectLayer.Group ? data.value && <TableCellBool value={data.value} /> : <></>,
      },
      {
        field: "status",
        headerName: t("common:status"),
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        type: "singleSelect",
        valueOptions: filtersData.statusFilterOptions.map((x) => ({ value: x, label: GetObjectStatusLabel(x) })),
        renderCell: (data: Row) =>
          data?.row?.asset?.layer !== ObjectLayer.Group ? (
            <AssetStatus
              small
              status={data.value}
              tooltip={
                data.value === ObjectStatus.Rejected || data.value === ObjectStatus.RejectedAuto
                  ? GetMetadataByTagName(data?.row?.asset, "ingestResultMessage", metadata)?.value
                  : undefined
              }
            />
          ) : (
            <></>
          ),
      },
      {
        field: "actions",
        headerName: "",
        minWidth: CellSize.actionsDefault,
        flex: CellSize.actionsDefault,
        type: "actions",
        align: "right",
        getActions: (params: GridRowParams) => {
          if (!params || !params.row) return;
          let disabled =
            params?.row?.status !== ObjectStatus.Accepted &&
            params?.row?.status !== ObjectStatus.Rejected &&
            params?.row?.status !== ObjectStatus.WaitingForMEZZReference &&
            params?.row?.status !== ObjectStatus.QualityCheckInProgress &&
            params?.row?.status !== ObjectStatus.NeedCheckOrEditing &&
            params?.row?.status !== ObjectStatus.ToBeQCed;

          let btns: any[] = [];

          if (aclOrder && !disabled && params?.row?.status !== ObjectStatus.NeedCheckOrEditing)
            btns.push(
              <GridActionsCellItem
                icon={<ShoppingBagOutlinedIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                label={t("common:addToBasket")}
                title={t("common:addToBasket")}
                onClick={() => addObjectByIdToBasket(params?.row?.id)}
              />
            );

          if (!disabled) {
            btns.push(
              <GridActionsCellItem
                icon={<BookmarkBorderIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                label={t("button:addToCollection_one")}
                title={t("button:addToCollection_one")}
                onClick={() => AddItemsToCollection([params.row])}
              />
            );

            if (
              (params?.row?.objectType === ObjectType.Video ||
                params?.row?.objectType === ObjectType.Audio ||
                params?.row?.objectType === ObjectType.Image ||
                params?.row?.objectType === ObjectType.Subtitle ||
                params?.row?.objectType === ObjectType.ImfPackage ||
                params?.row?.objectType === ObjectType.DcpPackage) &&
              !disabled
            )
              btns.push(
                <GridActionsCellItem
                  icon={<PlayArrowOutlined sx={{ width: 20, height: 20, color: "text.primary" }} />}
                  label={t("common:preview")}
                  title={t("common:preview")}
                  onClick={() => {
                    if (params?.row?.objectType !== ObjectType.Image) {
                      if (!openModal) return;

                      openModal<ModalPreviewProps>(ModalPreview, {
                        objectId: Number(params?.row?.id),
                        episodeNb: params?.row?.objectType === ObjectType.Subtitle ? params?.row?.ep : null,
                        type: params?.row?.objectType,
                        closeModal: () => {},
                      });
                    } else {
                      setPosterViewerVisible(params?.row?.id);
                    }
                  }}
                />
              );
            if (params?.row?.objectType === ObjectType.TimetoolSequence && aclTimetool)
              btns.push(
                <GridActionsCellItem
                  icon={<ClearAllIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                  label={t("common:openInTimetoolLowerCase")}
                  title={t("common:openInTimetoolLowerCase")}
                  onClick={() => {
                    navigate(`${routePaths.timetool.path}/${params?.row?.id}`);
                  }}
                />
              );
          }

          if (params?.row?.asset?.layer === ObjectLayer.Group && params?.row?.asset?.childObjects?.length > 0) {
            btns.push(<ExpandableButton id={params?.row?.id} />);
          }
          return btns;
        },
      },
    ],
    [title, filtersData, addObjectByIdToBasket]
  );

  const handleSelectionModelChange = (newSelectedRows: GridRowSelectionModel) => {
    const selectedIds = newSelectedRows as number[];
    setSelectedItems(flattenAssets?.filter((x) => selectedIds.includes(x.id)) ?? []);

    const newAssetIdAdded = selectedIds.find((item) => !selectedItems.map((x) => x.id).includes(item));
    if (newAssetIdAdded) {
      const newAddedAsset = flattenAssets?.find((x) => x.id === newAssetIdAdded);
      if (newAddedAsset!.layer === ObjectLayer.Group)
        setSelectedItems((prevItems) => {
          const newItems =
            flattenAssets?.filter((x) => newAddedAsset?.childObjects?.map((x) => x.id).includes(x.id)) ?? [];
          const combinedItems = [...prevItems, ...newItems];
          const distinctItems = Array.from(new Map(combinedItems.map((item) => [item.id, item])).values());
          return distinctItems;
        });
    }

    const selectedAssets = GetChildsRecursivelyByIds(title || null, selectedIds);
    setCanSelectedItemsBeShared(AreAllObjectsShareable(selectedAssets));
  };

  // Top filters
  const updateSearchValue = React.useCallback((newSearchValue: string) => {
    setSearchValue(newSearchValue);
  }, []);

  const debouncedUpdateSearchValue = React.useMemo(() => debounce(500, updateSearchValue), [updateSearchValue]);

  const handleSearchValueChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchValue = event.target.value;
      debouncedUpdateSearchValue(newSearchValue);
    },
    [debouncedUpdateSearchValue]
  );

  const handleFilterByContent = (value: ContentFilterObject[]) => {
    setContentFilter(value);
  };

  const handleFilterByType = (value: ObjectType[]) => {
    setTypeFilter(value);
  };

  const handleFilterBySubType = (value: string[]) => {
    setSubTypeFilter(value);
  };

  const handleFilterByClass = (value: ObjectClass[]) => {
    setClassFilter(value);
  };

  const handleFilterByStatus = (value: ObjectStatus[]) => {
    setStatusFilter(value);
  };

  // Asset panel
  const hideAssetPanel = () => {
    setOpenAssetPanel(false);
  };

  const handleRowClick = (e: GridRowParams) => {
    if (e.row.asset.layer === ObjectLayer.Group && e.row.asset.childObjects.length > 0) {
      const isRowExpanded = (apiGrid.current.getRowNode(e.row.id) as any)?.childrenExpanded;
      apiGrid.current.setRowChildrenExpansion(e.row.id, !isRowExpanded as boolean);
    } else {
      showAssetPanel(e);
    }
  };

  const showAssetPanel = (e: GridRowParams) => {
    setClickedAssetId(e.row.id);
    setOpenAssetPanel(true);
  };
  // #endregion

  // Memos
  const flattenContents: DbAssetObjectInfos[] | null = useMemo(() => {
    return FlattenObjectListFromGraph(title ?? null, [ObjectLayer.Content]);
  }, [title]);

  const flattenAssets: DbAssetObjectInfos[] | null = useMemo(() => {
    return (
      FlattenObjectListFromGraph(
        title ?? null,
        [ObjectLayer.Asset, ObjectLayer.Group],
        [ObjectType.DcpPackage, ObjectType.ImfPackage]
      )?.filter((x) => x.type !== ObjectType.BundlePackage && x.type !== ObjectType.BundleComponent) ?? null
    );
  }, [title]);

  // Hooks
  React.useEffect(() => {
    // Waiting for redux store
    if (!title) return;

    if (flattenContents !== null) {
      setFiltersData((prevState) => {
        return {
          ...prevState,
          contentFilterOptions: flattenContents
            .map((content) => ({
              key: content.id,
              contentName: content.name || "",
              childNumber: CountAssetsInContent(content) || 0,
              isDeleted: content.isDeleted || false,
            }))
            .sort(),
        };
      });
    }

    if (flattenAssets !== null) {
      setFiltersData((prevState) => {
        return {
          ...prevState,
          typeFilterOptions: flattenAssets
            .flatMap((x) => x.type)
            .filter(Distinct)
            .sort(),
          subTypeFilterOptions: flattenAssets
            .flatMap((x) => GetMetadataByTagName(x, "contentSubType", metadata)?.value)
            .filter(Distinct)
            .sort(),
          classFilterOptions: flattenAssets
            .filter((x) => x.classification !== null)
            .flatMap((x) => x.classification!)
            .filter(Distinct)
            .sort(),
          statusFilterOptions: flattenAssets
            .flatMap((x) => x.status)
            .filter(Distinct)
            .sort(),
        };
      });

      setDataGrid(mapDataToGrid(flattenAssets, title));
    }
  }, [isLoading, title, flattenContents, flattenAssets]);

  const openCreateContentModal = () => {
    if (!openModal) return;
    openModal<ModalCreateContentElementProps>(ModalCreateContent, {
      titleId: title!.id,
    });
  };

  const filteredRows = useMemo(() => {
    let types = [...typeFilter];
    let classes = [...classFilter];

    if (classFilter.includes(ObjectClass.Dcp)) {
      // Include DCP group
      types.push(ObjectType.DcpPackage);
      types.push(ObjectType.GroupMasterDcp);
      classes.push(ObjectClass.Group);
    }
    if (classFilter.includes(ObjectClass.Imf)) {
      // Include IMF group
      types.push(ObjectType.ImfPackage);
      types.push(ObjectType.GroupMasterImf);
      classes.push(ObjectClass.Group);
    }

    let filteredRows =
      rows?.filter(
        (x) =>
          (x.contentName?.includesAllWords(searchValue) ||
            x.format?.includesAllWords(searchValue) ||
            x.name?.includesAllWords(searchValue) ||
            x.status?.includesAllWords(searchValue) ||
            x.id.toString().includes(searchValue)) &&
          (classes.length > 0 ? classes.includes(x.objectClass) : true) &&
          (types.length > 0 ? types.includes(x.objectType) : true) &&
          (subTypeFilter.length > 0 ? subTypeFilter.includes(x.contentSubType) : true) &&
          (contentFilter.length > 0
            ? contentFilter.flatMap((c) => c.contentName).includes(x.contentName) &&
              contentFilter.flatMap((c) => c.key).includes(x.contentId)
            : true) &&
          (statusFilter.length > 0 ? statusFilter.includes(x.status) : true) &&
          (recoveryFilter === false || x.isLegacyImport) &&
          (byPassFilter === false || x.isIngestQCBypassed) &&
          (fullQcFilter === false || x.isFullQcDone) &&
          (videoRefFilter === false || x.ref)
      ) ?? [];

    return filteredRows;
  }, [
    rows,
    searchValue,
    classFilter,
    typeFilter,
    subTypeFilter,
    contentFilter,
    statusFilter,
    recoveryFilter,
    byPassFilter,
    fullQcFilter,
    videoRefFilter,
  ]);

  const additionalFilters = (
    <>
      <FormMultiSelect
        objectName="contents"
        value={contentFilter}
        setValue={setContentFilter}
        options={filtersData.contentFilterOptions.sort(function (a, b) {
          if (a.contentName.isEmpty()) return 1;
          return a.contentName.localeCompare(b.contentName);
        })}
        renderOption={(option) => option.contentName + " (" + option.childNumber + " items)"}
        renderInput={(option) => option.contentName}
        startIcon={<Inventory2Outlined />}
      />
      <FormMultiSelect
        objectName="types"
        value={typeFilter}
        setValue={setTypeFilter}
        options={filtersData.typeFilterOptions}
        renderOption={(option) => option}
        renderInput={(option) => option}
        startIcon={<WorkspacesOutlined />}
      />
      <FormMultiSelect
        objectName="subtypes"
        value={subTypeFilter}
        setValue={setSubTypeFilter}
        options={filtersData.subTypeFilterOptions}
        renderOption={(option) => option}
        renderInput={(option) => option}
        startIcon={<WorkspacesOutlined />}
      />
      <FormMultiSelect
        objectName="classes"
        value={classFilter}
        setValue={setClassFilter}
        options={filtersData.classFilterOptions}
        renderOption={(option) => option}
        renderInput={(option) => option}
        startIcon={<HighQualityOutlined />}
      />
      <FormMultiSelect
        objectName="statuses"
        value={statusFilter}
        setValue={setStatusFilter}
        options={filtersData.statusFilterOptions}
        renderOption={(option) => option}
        renderInput={(option) => option}
        startIcon={<BarChartOutlined />}
      />
      <Stack
        direction="row"
        gap={0.5}
        height={36}
        sx={(t) => ({ pl: 2, background: t.palette.backgroundAlpha.main, borderRadius: 1 })}
      >
        <FormGroup>
          <FormControlLabel
            control={<Checkbox value={fullQcFilter} onClick={() => setFullQcFilter((prev) => !prev)} />}
            label={<Typography variant="body2">{t("common:fullQC")}</Typography>}
          />
        </FormGroup>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox value={recoveryFilter} onClick={() => setRecoveryFilter((prev) => !prev)} />}
            label={<Typography variant="body2">{t("common:legacyImport")}</Typography>}
          />
        </FormGroup>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox value={byPassFilter} onClick={() => setByPassFilter((prev) => !prev)} />}
            label={<Typography variant="body2">{t("common:bypassed")}</Typography>}
          />
        </FormGroup>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox value={videoRefFilter} onClick={() => setVideoRefFilter((prev) => !prev)} />}
            label={<Typography variant="body2">{t("field:label.videoReference")}</Typography>}
          />
        </FormGroup>
      </Stack>
    </>
  );

  return (
    <>
      {isLoading || !title ? (
        <TableSkeleton />
      ) : (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={(t) => ({
              background: t.palette.background.paper,
              borderRadius: 1.5,
              p: 2,
              my: 2,
              alignItems: "center",
            })}
          >
            <PosterImage img={{ src: thumbnailService.GetObjectPosterUrl(title?.id) }} width={32} height={48} mr={2} />
            <Stack>
              <Typography fontWeight={500}>{title?.name}</Typography>
              <Typography color="text.tier">
                {t("page:body.countAssets", { count: CountChildRecursivelyObjectById(title) })}
              </Typography>
            </Stack>
            <Stack direction="row" ml="auto" alignItems="center" spacing={1}>
              <Button
                onClick={() =>
                  apiGrid.current.exportDataAsCsv({ delimiter: GlobalConst.EXPORT_DELIMITER, allColumns: true })
                }
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadOutlinedIcon />}
              >
                {t("common:export")}
              </Button>
              {UserHasAcl(AclTypes.aclIngestPlaceholderCreate) && (
                <Button color="primary" variant="contained" onClick={openCreateContentModal} startIcon={<AddIcon />}>
                  {t("button:createNewContent")}
                </Button>
              )}

              <ToggleButtonGroup
                exclusive
                value={selectedDisplayTab}
                aria-label="Treeview"
                onChange={(e, value) => setSelectedDisplayTab(value)}
                size="small"
              >
                <ToggleButton value="tree" aria-label="Tree view">
                  <AccountTreeOutlinedIcon />
                </ToggleButton>
                <ToggleButton value="table" aria-label="List view">
                  <TableRowsOutlinedIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
          {(selectedDisplayTab === "tree" && (
            <Grid container columnSpacing={5} position="relative">
              <Grid size={8}>
                {/* <TitleTreeView
                                    onSelect={onSelect}
                                    showHighlight
                                    displayFirstObject={false}
                                    graph={title!}
                                    searchTerm={searchValue}
                                    sx={{ mt: 1 }}
                                /> */}
                <AssetTreeView rootObjectId={title.id} context={TreeContext.AssetHierarchy} multiselect hideRoot />
              </Grid>
              <Grid size={4} position="relative" pt={1} pb={3}>
                <Box position="sticky" height="100%" top={{ top: "16px", maxHeight: "calc(100vh - 250px)" }}>
                  <TreeViewActions selection={selectedTreeItems} graph={title} />
                </Box>
              </Grid>
            </Grid>
          )) ||
            (selectedDisplayTab === "table" && (
              <Stack minHeight={"calc(100vh - 129px)"}>
                <DataTable
                  tableKey={TableUuids.TITLE_SINGLE_ASSETS}
                  rows={filteredRows}
                  columns={columns}
                  additionalFilters={additionalFilters}
                  getRowHeight={() => "auto"}
                  getEstimatedRowHeight={() => 200}
                  checkboxSelection
                  disableRowSelectionOnClick
                  pagination={true}
                  onRowClick={handleRowClick}
                  selectedRows={selectedItems?.map((x) => x.id)}
                  onRowSelectionModelChange={handleSelectionModelChange}
                  initialColumnVisibility={{
                    ep: isSeason,
                    isLegacyImport: false,
                    isIngestQCBypassed: false,
                    isFullQcDone: false,
                    contentName: false,
                    contentSubType: false,
                  }}
                  sx={{ mt: 1 }}
                  treeData
                  getTreeDataPath={getTreeDataPath}
                  groupingColDef={CUSTOM_GROUPING_COL_DEF}
                  isGroupExpandedByDefault={() => true}
                  pageSize={20}
                  apiRef={apiGrid}
                  initialState={{
                    pinnedColumns: {
                      left: ["__check__", "id", "name"],
                      right: ["actions"],
                    },
                  }}
                />
                <AssetPanel
                  open={openAssetPanel}
                  onClose={hideAssetPanel}
                  closePanel={hideAssetPanel}
                  assetId={clickedAssetId}
                  rowId={clickedAssetId}
                  reload={clickedAssetId}
                />
                <Viewer
                  visible={posterViewerVisible !== undefined}
                  noNavbar={true}
                  zIndex={2001}
                  onClose={() => {
                    setPosterViewerVisible(undefined);
                  }}
                  images={[
                    {
                      src: posterViewerVisible
                        ? mediaService.GetImagePreviewer(posterViewerVisible, cacheService.GetAuthKey() ?? "")
                        : "",
                      alt: "Image viewer #" + posterViewerVisible + " ",
                    },
                  ]}
                />
              </Stack>
            ))}
        </>
      )}
    </>
  );
}

export default AdminTitleAssets;
